import BarrelUser from "../model/BarrelUser";

const RolePower: Record<string, number> = {
    // new roles: king > queen > rook > knight > pawn
    king: 4,
    queen: 3,
    rook: 2,
    knight: 1,
    pawn: 0,
    // old roles: owner > admin > user
    owner: 2,
    admin: 1,
    user: 0
};

const noSort = <T>(_: T, __: T) => 0;

const sortBy = {
    role(prev: BarrelUser, cur: BarrelUser): number {
        if (prev.role !== cur.role) {
            return RolePower[cur.role] - RolePower[prev.role];
        }

        if (prev.user.status !== "invited" && cur.user.status === "invited") {
            return -1;
        } else if (prev.user.status === "invited" && cur.user.status !== "invited") {
            return 1;
        } else if (prev.user.status === "invited" && cur.user.status === "invited") {
            if (prev.user.email! < cur.user.email!) {
                return -1;
            }
            return 1;
        }

        if (prev.user.username! < cur.user.username!) {
            return -1;
        }

        return 1;
    },
    relativeOrder(sortOrder: Record<string, number>): (a: string, b: string) => number {
        return (a: string, b: string) => {
            if (a !== b) {
                if (sortOrder[a] < sortOrder[b]) {
                    return -1;
                }

                if (sortOrder[a] > sortOrder[b]) {
                    return 1;
                }

                return 0;
            }

            return 0;
        };
    },
    date: sortByDate,
    created: createSortByDate<{ created: string; }>(({ created }) => created),
    updated: createSortByDate<{ updated: string; }>(({ updated }) => updated),
    authorized: createSortByDate<{ authorizedAt: string; }>(({ authorizedAt }) => authorizedAt),
    naturally: (a: string, b: typeof a) => sortByProperty(a, b, object => object),
    name: (a: { name: string; }, b: typeof a) => sortByProperty(a, b, ({ name }) => name),
    value: (a: { value: string; }, b: typeof a) => sortByProperty(a, b, ({ value }) => value),
    xAxis: (a: { x: number; y: number; }, b: typeof a) => (a.x === b.x ? a.y - b.y : a.x - b.x),
    yAxis: (a: { x: number; y: number; }, b: typeof a) => (a.y === b.y ? a.x - b.x : a.y - b.y),
    statusTags: noSort
};

function createSortByDate<T>(getValue: (object: T) => string): (a: T, b: T) => number {
    return (a: T, b: T) => sortByDate(a, b, getValue);
}

function sortByDate<T>(a: T, b: T, getValue: (obj: T) => string): number {
    const prv = getValue(a);
    const cur = getValue(b);

    return new Date(cur).getTime() - new Date(prv).getTime();
}

const stringPropComparisonCache: Record<string, Record<string, number>> = {};

function cacheComparisonResult(prv: string, cur: string, result: number): number {
    if (stringPropComparisonCache[prv]) {
        stringPropComparisonCache[prv][cur] = result;
        return result;
    }
    stringPropComparisonCache[prv] = { [cur]: result };
    return result;
}

function sortByProperty<T>(a: T, b: T, getValue: (obj: T) => string): number {
    const prv = getValue(a);
    const cur = getValue(b);

    if (stringPropComparisonCache[prv] && cur in stringPropComparisonCache[prv]) {
        return stringPropComparisonCache[prv][cur];
    }

    const prvParts = prv.toLowerCase().match(/\D+|\d+/g)!;
    const curParts = cur.toLowerCase().match(/\D+|\d+/g)!;
    const partLen = Math.min(prvParts.length, curParts.length);

    const collator = new Intl.Collator();

    for (let i = 0; i < partLen; i++) {
        if (prvParts[i] === curParts[i]) {
            continue;
        }

        const nPrv = parseInt(prvParts[i], 10);
        const nCur = parseInt(curParts[i], 10);

        if (isNaN(nPrv) || isNaN(nCur)) {
            return cacheComparisonResult(prv, cur, collator.compare(prvParts[i], curParts![i]));
        }

        return cacheComparisonResult(prv, cur, nPrv - nCur);
    }

    return cacheComparisonResult(prv, cur, prvParts.length - curParts.length);
}

export {
    sortBy,
    createSortByDate
};
